import {z} from 'zod';
import * as forms from '../_components';
import * as constants from '../constants';
import * as i18n from '../i18n';
import * as schemas from '../schemas';
import type {RegistryEntry} from '../types';

export const REGISTRY = {
  // 4E-form-1
  ContactBillingForm: {
    id: '888a6fb6-d050-46b6-afc5-bcc8a76e4a16',
    slug: '/contact-billing/',
    component: forms.ContactBillingForm,
    title: 'forms.contactBilling.title',
    description: '',
    includeInSitemap: false,
    schema: z.object({
      firstName: schemas.requiredString(50),
      lastName: schemas.requiredString(50),
      email: schemas.requiredEmail,
      street: schemas.requiredString(100),
      apt: schemas.optionalString(20),
      zipCode: schemas.requiredZipCode,
      questionType: schemas.requiredChoice(i18n.QUESTION_TYPES),
      description: schemas.requiredString(1000),
      attachments: schemas.optionalFileList,
      formType: schemas.optionalString(),
      language: schemas.optionalString(),
    }),
    recaptchaAction: 'submit_form_contact_billing',
  },
  // 4E-form-2
  ContactSalesForm: {
    id: '08f0ee8b-90fb-4473-82b8-d25a07b712c0',
    slug: '/contact-sales/',
    component: forms.ContactSalesForm,
    title: 'forms.contactSales.title',
    description: '',
    includeInSitemap: false,
    schema: z.object({
      firstName: schemas.requiredString(50),
      lastName: schemas.requiredString(50),
      email: schemas.requiredEmail,
      street: schemas.requiredString(100),
      apt: schemas.optionalString(20),
      zipCode: schemas.requiredZipCode,
      phoneNumber: schemas.optionalPhoneNumber,
      propertyType: schemas.requiredChoice(i18n.PROPERTY_TYPES),
      description: schemas.requiredString(1000),
      attachments: schemas.optionalFileList,
      formType: schemas.optionalString(),
      language: schemas.optionalString(),
    }),
    recaptchaAction: 'submit_form_contact_gfiber',
  },
  // 4E-form-3
  AddressVerificationForm: {
    id: '7004dcc3-2ab3-407c-aa90-fe6ecf8e6972',
    slug: '/address-verification/',
    component: forms.AddressVerificationForm,
    title: 'forms.addressVerification.title',
    description: '',
    includeInSitemap: false,
    schema: z.object({
      firstName: schemas.requiredString(50),
      lastName: schemas.requiredString(50),
      email: schemas.requiredEmail,
      forcedCancellation: schemas.requiredChoice(i18n.YES_NO),
      billingAddress: schemas.requiredString(1000),
      ticketId: schemas.requiredString(1000),
      verificationDocuments: schemas.requiredFileList,
      formType: schemas.optionalString(),
      language: schemas.optionalString(),
    }),
    recaptchaAction: 'submit_form_address_verification',
  },
  // 4E-form-5
  DataRequestForm: {
    id: '7f0c1e8b-4d2a-4a6c-9b3e-8f5d1c2b4a6d',
    slug: '/fiber-data-access/',
    component: forms.DataRequestForm,
    title: 'forms.dataRequest.title',
    description: '',
    includeInSitemap: false,
    schema: z.object({
      accountId: schemas.optionalString(50),
      email: schemas.requiredEmail,
      firstName: schemas.requiredString(50),
      lastName: schemas.requiredString(50),
      street: schemas.requiredString(100),
      apt: schemas.optionalString(20),
      city: schemas.requiredString(50),
      state: schemas.requiredChoice(constants.DATA_REQUEST_STATE_CODES),
      zipCode: schemas.requiredZipCode,
      typeOfRequest: schemas.requiredChoice(i18n.REQUEST_TYPES),
      typeOfResponse: schemas.requiredChoice(i18n.RESPONSE_TYPES),
      requesterRelationship: schemas.requiredChoice(i18n.RELATIONSHIPS),
      formType: schemas.optionalString(),
      language: schemas.optionalString(),
    }),
    recaptchaAction: 'submit_form_fiber_data_access',
  },
  // 4E-form-6
  FiberDeceasedForm: {
    id: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
    slug: '/fiber-deceased/',
    component: forms.FiberDeceasedForm,
    title: 'forms.fiberDeceased.title',
    description: '',
    includeInSitemap: false,
    schema: z
      .object({
        firstName: schemas.optionalString(50),
        lastName: schemas.optionalString(50),
        phoneNumber: schemas.requiredPhoneNumber,
        email: schemas.requiredEmail,
        legalStatus: schemas.requiredChoice(i18n.STATUS_OPTIONS),
        dateOfDeath: schemas.optionalDateNotFuture,
        depCustomerStatus: schemas.optionalChoice(i18n.YES_NO),
        deathCert: schemas.optionalFileList,
        depIncapDate: schemas.optionalDateNotFuture,
        legalAuthorizationDoc: schemas.optionalFileList,
        identityVerificationDoc: schemas.optionalFileList,
        accountAction: schemas.requiredChoice(i18n.ACTION_OPTIONS),
        transferRecipientEmail: schemas.optionalString(254),
        fullName: schemas.requiredString(150),
        gfiberAccount: schemas.requiredEmail,
        homeAddress: schemas.requiredString(150),
        state: schemas.requiredState,
        attachments: schemas.optionalFileList,
        formType: schemas.optionalString(),
        language: schemas.optionalString(),
      })
      .refine(
        data =>
          !(
            data.legalStatus === i18n.LEGAL_STATUS.DECEASED &&
            !data.depCustomerStatus
          ),
        {
          message: constants.VALIDATION_MESSAGES.REQUIRED,
          path: ['depCustomerStatus'],
        },
      )
      .refine(
        data =>
          !(
            data.legalStatus === i18n.LEGAL_STATUS.DECEASED && !data.dateOfDeath
          ),
        {
          message: constants.VALIDATION_MESSAGES.REQUIRED,
          path: ['dateOfDeath'],
        },
      )
      .refine(
        data =>
          !(
            data.legalStatus === i18n.LEGAL_STATUS.DECEASED &&
            (!data.deathCert || data.deathCert.length === 0)
          ),
        {
          message: constants.VALIDATION_MESSAGES.REQUIRED,
          path: ['deathCert'],
        },
      )
      .refine(
        data =>
          !(
            data.legalStatus === i18n.LEGAL_STATUS.INCAPACITATED &&
            !data.depIncapDate
          ),
        {
          message: constants.VALIDATION_MESSAGES.REQUIRED,
          path: ['depIncapDate'],
        },
      )
      .refine(
        data =>
          !(
            data.legalStatus === i18n.LEGAL_STATUS.INCAPACITATED &&
            (!data.legalAuthorizationDoc ||
              data.legalAuthorizationDoc.length === 0)
          ),
        {
          message: constants.VALIDATION_MESSAGES.REQUIRED,
          path: ['legalAuthorizationDoc'],
        },
      )
      .refine(
        data =>
          !(
            data.legalStatus === i18n.LEGAL_STATUS.INCAPACITATED &&
            (!data.identityVerificationDoc ||
              data.identityVerificationDoc.length === 0)
          ),
        {
          message: constants.VALIDATION_MESSAGES.REQUIRED,
          path: ['identityVerificationDoc'],
        },
      )
      .refine(
        data => {
          if (data.accountAction === i18n.ACCOUNT_ACTION.TRANSFER) {
            const emailSchema = z.email();
            return (
              !!data.transferRecipientEmail &&
              emailSchema.safeParse(data.transferRecipientEmail).success
            );
          }
          return true;
        },
        {
          message: constants.VALIDATION_MESSAGES.REQUIRED,
          path: ['transferRecipientEmail'],
        },
      ),
    recaptchaAction: 'submit_form_fiber_deceased',
  },
  // 4E-form-7
  ContactConstructionForm: {
    id: 'ce556cb7-41e1-45b8-8e73-74d2425b780b',
    slug: '/construction-escalations/',
    component: forms.ContactConstructionForm,
    title: 'forms.contactConstruction.title',
    description: '',
    includeInSitemap: false,
    schema: z.object({
      firstName: schemas.requiredString(50),
      lastName: schemas.requiredString(50),
      email: schemas.requiredEmail,
      phoneNumber: schemas.optionalPhoneNumber,
      dateAndTime: schemas.requiredString(100),
      street: schemas.requiredString(100),
      apt: schemas.optionalString(20),
      zipCode: schemas.requiredZipCode,
      description: schemas.requiredString(1000),
      attachments: schemas.optionalFileList,
      formType: schemas.optionalString(),
      language: schemas.optionalString(),
    }),
    recaptchaAction: 'submit_form_contact_construction',
  },
  // 4E-form-8
  FiberSocialForm: {
    id: 'ba60f3c1-2974-4e87-9cff-15819b33665a',
    slug: '/fiber-social/',
    component: forms.FiberSocialForm,
    title: 'forms.fiberSocial.title',
    description: '',
    includeInSitemap: false,
    schema: z.object({
      firstName: schemas.requiredString(50),
      lastName: schemas.requiredString(50),
      email: schemas.requiredEmail,
      socialHandle: schemas.requiredString(100),
      address: schemas.optionalString(255),
      phoneNumber: schemas.optionalPhoneNumber,
      socialDetails: schemas.requiredString(1000),
      attachments: schemas.optionalFileList,
      formType: schemas.optionalString(),
      language: schemas.optionalString(),
    }),
    recaptchaAction: 'submit_form_fiber_social',
  },
  // 4E-form-9
  FiberInterestForm: {
    id: '3147a7b3-280f-4afb-aaf1-5d0e77a6b5a8',
    slug: '/fiber-interest/',
    component: forms.FiberInterestForm,
    title: 'forms.fiberInterest.title',
    description: '',
    includeInSitemap: false,
    schema: z.object({
      companyName: schemas.requiredString(50),
      propertyName: schemas.requiredString(100),
      street: schemas.requiredString(100),
      city: schemas.requiredString(50),
      state: schemas.requiredState,
      zipCode: schemas.requiredZipCode,
      numberOfUnits: schemas.requiredNumber,
      typeOfBuilding: schemas.requiredChoice(i18n.BUILDING_TYPES),
      isPropertyFullyBuilt: schemas.requiredChoice(i18n.YES_NO),
      additionalPropertyInfo: schemas.optionalString(1000),
      propertyRelationship: schemas.requiredChoice(i18n.PROPERTY_RELATIONSHIPS),
      firstName: schemas.requiredString(50),
      lastName: schemas.requiredString(50),
      email: schemas.requiredEmail,
      phoneNumber: schemas.requiredPhoneNumber,
      termsAndConditions: schemas.requiredCheckbox,
    }),
    recaptchaAction: 'submit_form_fiber_interest',
  },
  // 4E-form-10
  FiberInterestCityOfficialForm: {
    id: 'c2d110ab-ab92-4608-9bf2-3bba35c76cc0',
    slug: '/fiber-interest-city-official/',
    component: forms.FiberInterestCityOfficialForm,
    title: 'forms.fiberInterestCityOfficial.title',
    description: '',
    includeInSitemap: false,
    schema: z.object({
      iam: schemas.requiredChoice(i18n.I_AM),
      interest: schemas.optionalString(100),
      entityName: schemas.requiredString(100),
      role: schemas.requiredString(100),
      city: schemas.requiredString(50),
      state: schemas.requiredState,
      firstName: schemas.requiredString(50),
      lastName: schemas.requiredString(50),
      email: schemas.requiredEmail,
      phoneNumber: schemas.requiredPhoneNumber,
      termsAndConditions: schemas.requiredCheckbox,
    }),
    recaptchaAction: 'submit_form_fiber_interest_city_official',
  },
  // 4E-form-11
  FiberBlogForm: {
    id: '482c4baa-e054-4fa1-8002-4e8289596dc8',
    slug: '/fiber-blog/',
    component: forms.FiberBlogForm,
    title: 'forms.fiberBlog.title',
    description: '',
    includeInSitemap: false,
    schema: z.object({}),
    recaptchaAction: '',
  },
  // 4E-form-16
  ContactGFiberForm: {
    id: '9c0c069a-dd14-48a0-a077-b08ce2b7cf0f',
    slug: '/contact-gfiber/',
    component: forms.ContactGFiberForm,
    title: 'forms.contactGFiber.title',
    description: '',
    includeInSitemap: false,
    schema: z.object({
      firstName: schemas.requiredString(50),
      lastName: schemas.requiredString(50),
      email: schemas.requiredEmail,
      street: schemas.requiredString(100),
      apt: schemas.optionalString(20),
      zipCode: schemas.requiredZipCode,
      description: schemas.requiredString(1000),
      attachments: schemas.optionalFileList,
      formType: schemas.optionalString(),
      language: schemas.optionalString(),
    }),
    recaptchaAction: 'submit_form_contact_gfiber',
  },
} satisfies Record<string, RegistryEntry>;
